import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../lib/toast';
import { Send, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { UserAvatar } from '@/components/shared';
import MentionSuggestions from '@/components/shared/MentionSuggestions';
import { formatTimeAgo } from '@/utils/timeFormat';
import { svcGet, svcPost, svcDelete, errorText } from '../lib/api';
import LoadFailed from './LoadFailed';

const MAX_LEN = 1000;

// Comments on a service (Section 4.9): plain text, @mentions from /mentionable
// (the people on this service plus admins, C-03), oldest first.
// Fix 6F (wherever it renders, reviewer addendum A2): a first load that fails
// shows PC3 with "Try again" and tells the parent (``onLoadFailedChange``) so
// its heading drops the count (A4); a failed refresh keeps the thread on
// screen; deleting asks PC4 first; the counter (PC5) shows at the limit.
export default function ServiceComments({ serviceId, userId, isAdmin, teamCount, onCountChange, onLoadFailedChange, closedReason = null, reloadKey = '' }) {
  const [comments, setComments] = useState(null);
  const [failed, setFailed] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [members, setMembers] = useState([]);
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([
        svcGet(`/${serviceId}/comments`, userId),
        svcGet(`/${serviceId}/mentionable`, userId),
      ]);
      setComments(c.comments || []);
      setMembers(m.members || []);
      setFailed(false);
    } catch {
      setFailed(true);   // PC3 only while nothing is on screen; a loaded thread stays as it is
    }
  }, [serviceId, userId]);

  const loadFailed = failed && comments === null;
  useEffect(() => { onLoadFailedChange?.(loadFailed); }, [loadFailed, onLoadFailedChange]);
  const retry = () => { setFailed(false); load(); };

  useEffect(() => {
    setComments(null);
    setFailed(false);
    setText('');
    setMentions([]);
    load();
  }, [load]);

  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  // L6-2: a service notification arriving reloads the thread (no timer, no socket).
  const firstStamp = useRef(reloadKey);
  useEffect(() => {
    if (reloadKey === firstStamp.current) return;
    firstStamp.current = reloadKey;
    load();
  }, [reloadKey, load]);

  const detectMention = (value, caret) => {
    const upto = value.slice(0, caret ?? value.length);
    const m = upto.match(/(?:^|\s)@(\w*)$/);
    setMentionQuery(m ? m[1] : null);
  };

  const onChange = (e) => {
    setText(e.target.value.slice(0, MAX_LEN));
    detectMention(e.target.value, e.target.selectionStart);
  };

  const onSelectMention = (item) => {
    if (!item || item.kind !== 'user') return;
    const el = inputRef.current;
    const caret = el?.selectionStart ?? text.length;
    const before = text.slice(0, caret).replace(/@(\w*)$/, () => `@${item.user_name} `);
    const next = (before + text.slice(caret)).slice(0, MAX_LEN);
    setText(next);
    setMentions((prev) => (prev.some((x) => x.user_id === item.user_id) ? prev : [...prev, { user_id: item.user_id, name: item.user_name }]));
    setMentionQuery(null);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(before.length, before.length);
    });
  };

  const send = async () => {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const live = mentions.filter((m) => content.includes(`@${m.name}`));
      const created = await svcPost(`/${serviceId}/comments`, userId, { content, mentions: live });
      if (comments === null) {
        load();   // the thread never loaded: fetch it whole rather than show this comment alone
      } else {
        setComments((prev) => {
          const next = [...(prev || []), created];
          onCountChange?.(next.filter((c) => !c.deleted).length);
          return next;
        });
      }
      setText('');
      setMentions([]);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSending(false);
    }
  };

  const remove = async (c) => {
    try {
      await svcDelete(`/comments/${c.id}`, userId);
      setComments((prev) => {
        const next = (prev || []).map((x) => (x.id === c.id ? { ...x, deleted: true, content: '', mentions: [] } : x));
        onCountChange?.(next.filter((x) => !x.deleted).length);
        return next;
      });
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  return (
    <div data-testid="service-comments">
      {/* U33 (v2.8): what this thread is for, in place of "Seen by the N people…" (still hidden with nobody on the plan, L11-8). */}
      {teamCount > 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-3" data-testid="comments-audience">
          Questions about this plan. Only people on this service and admins see these.
        </p>
      )}
      {loadFailed ? (
        <LoadFailed title={'Couldn\u2019t load comments'} onRetry={retry} testIdPrefix="comments" className="mb-3" />
      ) : comments === null ? (
        <div className="space-y-2">
          <div className="h-10 bg-slate-100 dark:bg-[#3a3d41] rounded-lg animate-pulse" />
          <div className="h-10 bg-slate-100 dark:bg-[#3a3d41] rounded-lg animate-pulse" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">No comments yet.</p>
      ) : (
        <div className="space-y-3 mb-3">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2.5" data-testid={`comment-${c.id}`}>
              <UserAvatar userId={c.user_id} name={c.user_name} size="xs" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{c.user_name}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{formatTimeAgo(c.created_at)}</span>
                  {!c.deleted && (c.user_id === userId || isAdmin) && (
                    <button
                      type="button"
                      onClick={() => setPendingDelete(c)}
                      className="ml-auto text-slate-400 hover:text-red-500"
                      aria-label="Remove comment"
                      data-testid={`comment-delete-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {c.deleted ? (
                  <p className="text-sm italic text-slate-500 dark:text-slate-400">comment removed</p>
                ) : (
                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">{c.content}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {closedReason ? (
        <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="comments-closed">{closedReason}</p>
      ) : (
      <div className="relative">
        <Textarea
          ref={inputRef}
          value={text}
          onChange={onChange}
          onKeyDown={(e) => {
            if (mentionQuery !== null && suggestionsRef.current?.handleKeyDown(e)) return;
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
          }}
          onBlur={() => setTimeout(() => setMentionQuery(null), 150)}
          placeholder="Write a comment… use @ to mention"
          rows={2}
          className="resize-none pr-11"
          data-testid="comment-input"
        />
        <MentionSuggestions
          ref={suggestionsRef}
          query={mentionQuery}
          members={members}
          groups={[]}
          onSelect={onSelectMention}
          currentUserId={userId}
        />
        <Button
          size="icon"
          className="absolute right-1.5 bottom-1.5 h-8 w-8 bg-teal-600 hover:bg-teal-700 text-white"
          onClick={send}
          disabled={!text.trim() || sending}
          aria-label="Send comment"
          data-testid="comment-send-btn"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
      )}
      {!closedReason && text.length >= MAX_LEN && (
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 text-right tabular-nums" data-testid="comment-counter">{text.length}/{MAX_LEN}</p>
      )}

      {/* PC4: deleting a comment asks first. */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="comment-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this comment? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="comment-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { const c = pendingDelete; setPendingDelete(null); if (c) remove(c); }}
              data-testid="comment-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
